import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as diagnosticApi from '@/apis/diagnostic';

export const QUERY_KEYS = {
  all: ['diagnostic'],
  lists: () => [...QUERY_KEYS.all, 'list'],
  list: (params) => [...QUERY_KEYS.lists(), params],
  details: () => [...QUERY_KEYS.all, 'detail'],
  detail: (id) => [...QUERY_KEYS.details(), id],
  categories: () => [...QUERY_KEYS.all, 'categories'],
  subCategories: (params) => [...QUERY_KEYS.all, 'sub-categories', params],
  detailWithCategories: (id) => [...QUERY_KEYS.details(), 'with-categories', id],
};

// --- Queries ---
// 1. 보안약점유형(카테고리) 목록 조회
export const useDiagnosticMainCategories = (options = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.categories(),
    queryFn: () => diagnosticApi.getDiagnosticMainCategories(),
    enabled: options.enabled !== false, // 기본값은 true, false로 명시하면 비활성화,
    refetchOnWindowFocus: false,
  });
};

// 2. 보안약점(서브카테고리) 목록 조회
export const useDiagnosticSubCategories = (mainCategoryId) => {
  return useQuery({
    queryKey: QUERY_KEYS.subCategories(mainCategoryId),
    queryFn: () => diagnosticApi.getDiagnosticSubCategories(mainCategoryId),
    enabled: mainCategoryId != null && mainCategoryId !== '' && !isNaN(Number(mainCategoryId)),
  });
};

// 3. 진단 및 대응훈련 문제 목록 조회
export const useDiagnosticProblems = (params, options = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.list(params),
    queryFn: () => diagnosticApi.getDiagnosticProblems(params),
    enabled: options.enabled !== false, // 기본값은 true, false로 명시하면 비활성화
  });
};

// 4. 진단 및 대응훈련 문제 상세 조회
export const useDiagnosticProblemDetail = (problemId) => {
  return useQuery({
    queryKey: QUERY_KEYS.detail(problemId),
    queryFn: () => diagnosticApi.getDiagnosticProblemDetail(problemId),
    enabled: !!problemId,
  });
};

// 5. 진단 및 대응훈련 문제 상세 조회 + 카테고리 및 서브카테고리 조회
export const useDiagnosticProblemDetailWithCategories = (problemId) => {
  return useQuery({
    queryKey: QUERY_KEYS.detailWithCategories(problemId),
    queryFn: async () => {
      const detail = await diagnosticApi.getDiagnosticProblemDetail(problemId);
      const [categories, subCategories] = await Promise.all([
        diagnosticApi.getDiagnosticMainCategories(),
        detail?.category?.id
          ? diagnosticApi.getDiagnosticSubCategories(detail.category.id)
          : Promise.resolve([]),
      ]);
      return { detail, categories, subCategories };
    },
    enabled: !!problemId,
  });
};

// --- Mutations ---
// 1. 진단 및 대응훈련 문제 등록
export const useCreateDiagnosticProblem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: diagnosticApi.createDiagnosticProblem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.lists() });
    },
  });
};

// 2. 진단 및 대응훈련 문제 수정
// mutateAsync(requestData) 또는 mutateAsync({ requestData }) 호출
export const useUpdateDiagnosticProblem = (problemId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => {
      const requestData = variables?.requestData ?? variables;
      return diagnosticApi.updateDiagnosticProblem(problemId, requestData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.detailWithCategories(problemId) });
    },
  });
};

// 3. 진단 및 대응훈련 문제 삭제
export const useDeleteDiagnosticProblem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: diagnosticApi.deleteDiagnosticProblem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.lists() });
    },
  });
};

// 4. 진단 및 대응훈련 문제 승인 여부 수정
export const useUpdateProblemApproval = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ problemId, approval }) => diagnosticApi.updateProblemApproval(problemId, approval),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.detail(variables.problemId) });
    },
  });
};